import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for live reporting controls (play/pause) in survey live reporting UI.
 * Integrates with ActionCable and another Stimulus controller for real-time updates.
 *
 * @class LiveReportingControlsController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * Survey ID value passed from the DOM.
   * @type {Number}
   */
  static values = { surveyId: Number }

  /**
   * Button target for play/pause control.
   * @type {string[]}
   */
  static targets = ["button"]

  /**
   * Called when the controller is connected to the DOM.
   * Logs the survey ID for debugging.
   */
  connect() {
    console.log('Live Reporting Controls connected for survey:', this.surveyIdValue)
    console.log('Connect - hasButtonTarget:', this.hasButtonTarget)
    if (this.hasButtonTarget) {
      const btn = this.buttonTarget
      console.log('Connect - Button found:', btn)
      console.log('Connect - Button title:', btn.getAttribute('title'))
      console.log('Connect - data-bs-toggle:', btn.getAttribute('data-bs-toggle'))
      console.log('Connect - Bootstrap available:', !!window.bootstrap)
      console.log('Connect - Bootstrap Tooltip:', typeof window.bootstrap?.Tooltip)
      
      // Check if Bootstrap has different API versions
      if (window.bootstrap && typeof window.bootstrap.Tooltip === "function") {
        console.log('Connect - Checking Bootstrap Tooltip API...')
        console.log('Connect - getInstance method:', typeof window.bootstrap.Tooltip.getInstance)
        
        // Try to check for existing instance safely
        try {
          if (typeof window.bootstrap.Tooltip.getInstance === "function") {
            const instance = window.bootstrap.Tooltip.getInstance(btn)
            console.log('Connect - Existing Bootstrap instance:', instance)
          } else {
            console.log('Connect - Bootstrap Tooltip.getInstance not available (older version?)')
          }
        } catch (e) {
          console.log('Connect - Error checking instance:', e.message)
        }

        // Initialize tooltip — guard against double-init on Turbo reconnect
        try {
          if (!window.bootstrap.Tooltip.getInstance?.(btn)) {
            const title = btn.getAttribute('title') || 'Auto-progression control'
            new window.bootstrap.Tooltip(btn, { title, placement: 'top' })
          }
        } catch (e) {}
      }

    }
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up tooltips to prevent stale tooltip content.
   */
  disconnect() {
    console.log('Live Reporting Controls disconnecting for survey:', this.surveyIdValue)
    // Dispose specific button tooltip before clearing all
    try {
      if (this.hasButtonTarget) {
        const existing = window.bootstrap?.Tooltip?.getInstance?.(this.buttonTarget)
        if (existing) existing.dispose()
      }
    } catch (e) {}
    // Clean up any existing tooltips when the Turbo Frame updates
    this.clearAllTooltips()
  }

  /**
   * Pauses live reporting for all users via ActionCable and updates UI state.
   * @param {Event} event
   */
  pause(event) {
    console.log('=== MANUAL PAUSE ACTION CALLED ===')
    // Get the current position from the button or URL
    const position = event.currentTarget.dataset.position || this.getCurrentPositionFromURL()
    console.log('Pause - position:', position)

    // Call the ActionCable pause method if available
    if (window.liveReportingSubscription) {
      console.log('Pause - calling ActionCable pause (manual)')
      window.liveReportingSubscription.pause(position, true) // true = manual pause
    }

    // Update the stream controller's live state immediately for responsive UI
    if (window.liveReportingController) {
      console.log('Pause - updating stream controller state')
      window.liveReportingController.liveValue = false
      window.liveReportingController.updateControlsState()
    }

    // Update the button immediately for better UX
    console.log('Pause - updating button to paused state')
    this.updateButton("paused")
    
    // Refresh the questions frame to show the warning message
    this.refreshQuestionsFrame()
  }

  /**
   * Pauses live reporting for navigation between questions (automatic pause).
   * @param {Event} event
   */
  pauseForNavigation(event) {
    console.log('=== NAVIGATION PAUSE ACTION CALLED ===')
    // Get the current position from the button or URL
    const position = event.currentTarget.dataset.position || this.getCurrentPositionFromURL()
    console.log('Navigation Pause - position:', position)

    // Call the ActionCable pause method if available (false = not manual)
    if (window.liveReportingSubscription) {
      console.log('Navigation Pause - calling ActionCable pause (automatic)')
      window.liveReportingSubscription.pause(position, false) // false = automatic pause
    }

    // Update the stream controller's live state immediately for responsive UI
    if (window.liveReportingController) {
      console.log('Navigation Pause - updating stream controller state')
      window.liveReportingController.liveValue = false
      window.liveReportingController.updateControlsState()
    }

    // Update the button immediately for better UX
    console.log('Navigation Pause - updating button to paused state')
    this.updateButton("paused")
  }

  /**
   * Resumes live reporting for all users via ActionCable and updates UI state.
   * @param {Event} event
   */
  resume(event) {
    console.log('=== RESUME ACTION CALLED ===')
    // Call the ActionCable resume method if available
    if (window.liveReportingSubscription) {
      console.log('Resume - calling ActionCable resume')
      window.liveReportingSubscription.resume()
    }

    // Update the stream controller's live state immediately for responsive UI
    if (window.liveReportingController) {
      console.log('Resume - updating stream controller state')
      window.liveReportingController.liveValue = true
      window.liveReportingController.updateControlsState()
    }

    // Update the button immediately for better UX
    console.log('Resume - updating button to live state')
    this.updateButton("live")
    
    // Refresh the questions frame to hide the warning message
    this.refreshQuestionsFrame()
  }

  /**
   * Refreshes the questions turbo frame to update the warning message visibility.
   */
  refreshQuestionsFrame() {
    const questionsFrame = document.querySelector(`turbo-frame[id*="live_reporting/questions"]`)
    if (questionsFrame) {
      console.log('Refreshing questions frame:', questionsFrame.id)
      // Use Turbo's programmatic refresh by resetting the src attribute
      if (questionsFrame.src) {
        const originalSrc = questionsFrame.src
        questionsFrame.src = originalSrc
        console.log('Questions frame src reset to:', originalSrc)
      } else {
        // Fallback: construct the URL manually
        const surveyId = this.surveyIdValue
        const newSrc = `/surveys/${surveyId}/live_reporting/questions`
        questionsFrame.src = newSrc
        console.log('Questions frame src set to:', newSrc)
      }
    } else {
      console.log('Questions frame not found')
    }
  }

  /**
   * Gets the current question position from the URL (for context-aware pausing).
   * @returns {string}
   */
  getCurrentPositionFromURL() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('position') || '1'
  }

  /**
   * Updates the play/pause button and icon to reflect the current state.
   * @param {string} state - Either "paused" or "live"
   */
  updateButton(state) {
    console.log(`=== UPDATE BUTTON TO: ${state.toUpperCase()} ===`)
    const button = this.buttonTarget
    const icon = button.querySelector("span")
    
    console.log('updateButton - button element:', button)
    console.log('updateButton - icon element:', icon)

    // Switch button action and icon based on state
    if (state === "paused") {
      console.log('updateButton - setting up PAUSED state (play button)')
      button.setAttribute("data-action", "click->live-reporting-controls#resume")
      const title = "Auto-progression is OFF. Click to resume."
      button.setAttribute("title", title)
      button.setAttribute("aria-label", title)
      button.classList.add("icon-button")
      icon.className = "icon-control-play small"
      console.log('updateButton - calling updateTooltipTitle with:', title)
      this.updateTooltipTitle(button, title)
    } else {
      console.log('updateButton - setting up LIVE state (pause button)')
      button.setAttribute("data-action", "click->live-reporting-controls#pause")
      const title = "Auto-progression is ON. Click to pause."
      button.setAttribute("title", title)
      button.setAttribute("aria-label", title)
      button.classList.remove("icon-button")
      icon.className = "icon-control-pause small"
      console.log('updateButton - calling updateTooltipTitle with:', title)
      this.updateTooltipTitle(button, title)
    }
  }

  /**
   * Updates tooltip title attributes and refreshes Bootstrap tooltip if it exists.
   * @param {HTMLElement} el
   * @param {string} title
   */
  updateTooltipTitle(el, title) {
    console.log('=== UPDATE TOOLTIP TITLE ===')
    console.log('updateTooltipTitle - element:', el)
    console.log('updateTooltipTitle - new title:', title)
    
    try {
      // Update all title attributes that Bootstrap might use
      el.setAttribute('title', title)
      el.setAttribute('data-original-title', title) // BS4
      el.setAttribute('data-bs-original-title', title) // BS5
      el.setAttribute('data-bs-title', title) // BS5 alternative
      
      console.log('updateTooltipTitle - updated all title attributes')

      // Check Bootstrap 5 with safer API detection
      if (window.bootstrap && typeof window.bootstrap.Tooltip === "function") {
        console.log('updateTooltipTitle - Bootstrap 5 available')
        
        let instance = null
        
        // Safely try to get existing instance
        try {
          if (typeof window.bootstrap.Tooltip.getInstance === "function") {
            instance = window.bootstrap.Tooltip.getInstance(el)
            console.log('updateTooltipTitle - existing instance found:', instance)
          } else {
            console.log('updateTooltipTitle - getInstance not available, checking for instance directly')
            // For older Bootstrap versions, check if tooltip was already initialized
            if (el._tooltip || el.__bootstrap_tooltip__) {
              instance = el._tooltip || el.__bootstrap_tooltip__
              console.log('updateTooltipTitle - found instance via direct property:', instance)
            }
          }
        } catch (e) {
          console.log('updateTooltipTitle - error getting instance:', e.message)
        }
        
        if (instance && instance._config) {
          console.log('updateTooltipTitle - updating existing instance config')
          instance._config.title = title
          console.log('updateTooltipTitle - instance config updated to:', instance._config.title)
        } else {
          console.log('updateTooltipTitle - no existing instance, creating new one')
          try {
            const newInstance = new window.bootstrap.Tooltip(el, {
              title: title,
              placement: 'top'
            })
            console.log('updateTooltipTitle - created new instance:', newInstance)
          } catch (err) {
            console.error('updateTooltipTitle - error creating instance:', err)
          }
        }
      } else {
        console.log('updateTooltipTitle - Bootstrap 5 not available')
      }

      // Check Bootstrap 4 with jQuery
      if (window.$ && typeof window.$(el).tooltip === "function") {
        console.log('updateTooltipTitle - Bootstrap 4 with jQuery available')
        try {
          const $el = window.$(el)
          $el.attr('data-original-title', title)
          
          // Check if tooltip is already initialized
          if ($el.data('bs.tooltip') || $el.data('tooltip')) {
            console.log('updateTooltipTitle - Bootstrap 4 instance found, updating')
            $el.tooltip('dispose').tooltip({
              title: title,
              placement: 'top'
            })
          } else {
            console.log('updateTooltipTitle - initializing Bootstrap 4 tooltip')
            $el.tooltip({
              title: title,
              placement: 'top'
            })
          }
        } catch (err) {
          console.error('updateTooltipTitle - Bootstrap 4 error:', err)
        }
      } else {
        console.log('updateTooltipTitle - no Bootstrap 4 with jQuery found')
      }
    } catch (e) {
      console.error('updateTooltipTitle - general error:', e)
      // Non-fatal; title attributes are updated
    }
  }

  /**
   * Refreshes Bootstrap tooltip text after updating the title attribute.
   * Supports Bootstrap 5 (no jQuery) and Bootstrap 4 (jQuery plugin) gracefully.
   * @param {HTMLElement} el
   * @param {string} title
   */
  refreshTooltip(el, title) {
    try {
      // Clear all existing tooltips first so we don't show stale content anywhere
      this.clearAllTooltips()

      // Always keep common attributes in sync for both BS4 and BS5
      el.setAttribute('title', title)
      el.setAttribute('data-original-title', title) // BS4
      el.setAttribute('data-bs-original-title', title) // BS5
      el.setAttribute('data-bs-title', title) // BS5 attribute alternative

      // Bootstrap 5 path (no jQuery)
      if (window.bootstrap && typeof window.bootstrap.Tooltip === "function") {
        const Tip = window.bootstrap.Tooltip
        // Instance was just cleared; create a fresh one
        let instance = new Tip(el)
        // Update config/title for future shows
        if (instance && instance._config) instance._config.title = title

        // If the pointer is still hovering, show the refreshed tooltip immediately
        if (el.matches && el.matches(':hover') && typeof instance.show === 'function') {
          instance.show()
        }
        return
      }

      // Bootstrap 4 path (jQuery plugin)
      if (window.$ && typeof window.$(el).tooltip === "function") {
        const $el = window.$(el)
        // Re-init clean and show if hovered
        $el.attr('data-original-title', title)
        $el.tooltip()
        if ($el.is(':hover')) {
          $el.tooltip('show')
        }
        return
      }
    } catch (e) {
      // Non-fatal; attribute is already updated.
    }
  }

  /**
   * Disposes all tooltips on the page (Bootstrap 5 and 4) to prevent stale content.
   */
  clearAllTooltips() {
    try {
      // Bootstrap 5: dispose instances on all tooltip-enabled elements
      if (window.bootstrap && typeof window.bootstrap.Tooltip === 'function') {
        const Tip = window.bootstrap.Tooltip
        const selector = '[data-bs-toggle="tooltip"],[data-toggle="tooltip"],[data-bs-original-title],[data-original-title]'
        document.querySelectorAll(selector).forEach(el => {
          const inst = Tip.getInstance(el)
          if (inst) {
            try { 
              inst.hide() 
              inst.dispose() 
            } catch (_) { }
          }
        })
      }

      // Bootstrap 4: hide and dispose via jQuery
      if (window.$ && typeof window.$.fn.tooltip === 'function') {
        const $all = window.$('[data-toggle="tooltip"],[data-bs-toggle="tooltip"]')
        try { 
          $all.tooltip('hide')
          $all.tooltip('dispose') 
        } catch (_) { }
      }

      // Force removal of any orphaned tooltip DOMs (works for both BS4 and BS5)
      document.querySelectorAll('.tooltip').forEach(node => {
        try {
          node.remove()
        } catch (_) { }
      })
      
    } catch (_) {
      // ignore
    }
  }

}
